import { Button, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useState } from "react";

import {
  getListAdCopiesQueryKey,
  useUpdateAdCopy,
} from "../../../api/generated/endpoints/generation/generation";
import type { AdCopy } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import classes from "./ad-copies.module.css";
import { CopyEditor } from "./CopyEditor";
import { draftChanged, draftOf, draftValid, elementsOf } from "./editing";

export interface CopyEditProps {
  teamId: string;
  brandId: string;
  copy: AdCopy;
  /** Cancel, or a save that answered: the pane shows the copy again. */
  onDone: () => void;
}

/**
 * The Ad copies modal's Edit (live's `copies-settings/edit-mode`, capture advanced-234/21): the
 * title ("Untitled"), Cancel, and Save, greyed until the copy is valid and changed; then live's
 * editor. A save replaces the copy's title and sections.
 */
export function CopyEdit({ teamId, brandId, copy, onDone }: CopyEditProps) {
  const queryClient = useQueryClient();
  const saved = draftOf(copy);
  const [draft, setDraft] = useState(saved);
  const update = useUpdateAdCopy({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getListAdCopiesQueryKey(teamId, brandId) });
        onDone();
      },
    },
  });
  const busy = update.isPending;
  return (
    <div className={classes.pane}>
      <div className={classes.paneHeader}>
        <TextInput
          style={{ flex: 1 }}
          aria-label="Title"
          placeholder="Untitled"
          value={draft.name}
          disabled={busy}
          onChange={(event) => {
            const name = event.currentTarget.value;
            setDraft((current) => ({ ...current, name }));
          }}
        />
        <Button variant="subtle" disabled={busy} onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="default"
          leftSection={<Check size={20} strokeWidth={1.5} />}
          loading={busy}
          disabled={!draftValid(draft) || !draftChanged(draft, saved)}
          onClick={() =>
            update.mutate({
              teamId,
              brandId,
              copyId: copy.id,
              data: { name: draft.name, text_elements: elementsOf(draft) },
            })
          }
        >
          Save
        </Button>
      </div>
      <div className={classes.paneBody}>
        <CopyEditor
          sections={draft.sections}
          disabled={busy}
          onChange={(sections) => setDraft((current) => ({ ...current, sections }))}
        />
        {update.isError ? <ErrorAlert error={update.error} /> : null}
      </div>
    </div>
  );
}
